import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, firestore } from "../../../configs/FirebaseConfig";
import { onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    loading: true,
    isAuthenticated: false,
    profile: null,
    profileLoading: true,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthState((prev) => ({
        ...prev,
        user,
        loading: false,
        isAuthenticated: !!user,
        profile: user ? prev.profile : null,
        profileLoading: !!user,
      }));
    });

    return () => unsubscribe();
  }, []);

  // Fetch user profile from Firestore when user is authenticated
  useEffect(() => {
    let unsubscribeProfile = null;

    if (authState.user?.uid) {
      const userDocRef = doc(firestore, "users", authState.user.uid);

      // Use onSnapshot for real-time updates to profile data
      unsubscribeProfile = onSnapshot(
        userDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            setAuthState((prev) => ({
              ...prev,
              profile: docSnapshot.data(),
              profileLoading: false,
            }));
          } else {
            setAuthState((prev) => ({
              ...prev,
              profile: null,
              profileLoading: false,
            }));
          }
        },
        (error) => {
          console.error("Error fetching user profile:", error);
          setAuthState((prev) => ({
            ...prev,
            profile: null,
            profileLoading: false,
          }));
        },
      );
    } else {
      setAuthState((prev) => ({
        ...prev,
        profile: null,
        profileLoading: false,
      }));
    }

    return () => {
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, [authState.user?.uid]);

  const signOut = () => firebaseSignOut(auth);

  const value = {
    ...authState,
    signOut,
    uid: authState.user?.uid,
    email: authState.user?.email,
    role: authState.profile?.userRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
